// VehicleEquipmentScene.js
// 战车装备管理场景
import InputHelper from './modules/InputHelper.js';
import UIStyleHelper from './modules/UIStyleHelper.js';
import VehicleEquipmentSystem from './modules/VehicleEquipmentSystem.js';
import FragmentSystem from './modules/FragmentSystem.js';
import MaterialSystem from './modules/MaterialSystem.js';
import DebugHelper from './modules/DebugHelper.js';
import InventoryHelper from './modules/InventoryHelper.js';
import BlueprintSystem from './modules/BlueprintSystem.js';
import GameStateStore from './modules/GameStateStore.js';

export default class VehicleEquipmentScene extends Phaser.Scene {
    constructor() {
        super('VehicleEquipmentScene');
    }
    
    init(data) {
        this.mode = data.mode || 'equip'; // equip: 装备, craft: 合成, disassemble: 分解
        this.previousSceneKey = data.previousSceneKey || 'WorldScene';
        this.playerData = GameStateStore.getPlayer();
        
        // 装备管理交互状态
        this.equipmentState = {
            selectedSlotIndex: 0,
            slotTexts: [],
            isActionMenuActive: false,
            isEquipableItemsMenuActive: false,
            currentSlot: null,
            currentEquipmentItem: null,
            equipableItemsList: [],
            equipableItemSelected: 0,
            actionMenuSelected: 0,
            optionTexts: [],
            // 合成菜单状态
            craftableEquipments: [],
            selectedCraftIndex: 0,
            craftTexts: [],
            // 分解菜单状态
            disassembleableEquipments: [],
            selectedDisassembleIndex: 0,
            disassembleTexts: [],
            disassembleMenuTexts: [], // 保存分解菜单的所有文本对象（包括材料统计、标题等）
            // 右侧属性文本引用
            statsTexts: {
                title: null,
                attack: null,
                defense: null,
                maxSp: null,
                dodgeRate: null,
                weightTitle: null,
                weight: null,
                ammoTitle: null,
                cannonAmmo: null,
                seAmmo: null,
                corrosionTitle: null,
                corrosionLayer: null,
                hint: null
            }
        };
    }
    
    create() {
        this.scene.bringToTop('VehicleEquipmentScene');
        this.cameras.main.setBackgroundColor(0x0a0a18);
        
        this.add.text(
            this.cameras.main.width / 2,
            36,
            '战车装备中心',
            {
                fontSize: '24px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontStyle: 'bold'
            }
        ).setOrigin(0.5);
        
        this.createModeMenu();
        this.createGuidePanel();
        
        if (this.mode === 'equip') {
            this.showEquipmentMenu();
        } else if (this.mode === 'craft') {
            this.showCraftMenu();
        } else if (this.mode === 'disassemble') {
            this.showDisassembleMenu();
        }
        
        this.setupControls();
    }

    _bindMenuInput(handlers, options = {}) {
        this._clearMenuInput();
        this._activeMenuInput = InputHelper.bindMenuKeyboard(this, handlers, options);
    }

    _clearMenuInput() {
        if (this._activeMenuInput) {
            this._activeMenuInput.destroy();
            this._activeMenuInput = null;
        }
    }

    createGuidePanel() {
        const hints = UIStyleHelper.MENU_HINTS;
        const guides = {
            equip: `【装备】${hints.NAVIGATE}选槽位 → ${hints.SELECT} → ${hints.CANCEL}`,
            craft: `【合成】${hints.NAVIGATE}选装备 → ${hints.SELECT}合成 → ${hints.CANCEL}`,
            disassemble: `【分解】${hints.NAVIGATE}选装备 → ${hints.SELECT}分解 → ${hints.CANCEL}`
        };

        if (this.guideText) {
            this.guideText.destroy();
        }

        this.guideText = this.add.text(
            this.cameras.main.width / 2,
            this.cameras.main.height - 28,
            guides[this.mode] || '',
            {
                fontSize: '13px',
                fill: '#888888',
                fontFamily: 'monospace',
                align: 'center'
            }
        ).setOrigin(0.5);
        this.guideText.setScrollFactor(0);
        this.guideText.setDepth(100);
    }
    
    createModeMenu() {
        const centerX = this.cameras.main.width / 2;
        const tabY = 78;
        const spacing = 130;

        const allTabs = [
            { key: 'equip', name: '装备管理' },
            { key: 'craft', name: '碎片合成' },
            { key: 'disassemble', name: '材料分解' }
        ];

        let tabs = allTabs;
        if (this.previousSceneKey !== 'MenuScene') {
            if (this.mode === 'craft') {
                tabs = [allTabs[1]];
            } else if (this.mode === 'disassemble') {
                tabs = [allTabs[2]];
            }
        }

        this.modeButtons = [];
        const startX = centerX - ((tabs.length - 1) * spacing) / 2;

        tabs.forEach((tab, index) => {
            const x = startX + index * spacing;
            const isActive = this.mode === tab.key;
            const button = this.add.text(x, tabY, tab.name, {
                fontSize: isActive ? '18px' : '16px',
                fill: isActive ? '#00ff88' : '#666666',
                fontFamily: 'monospace',
                fontStyle: isActive ? 'bold' : 'normal'
            }).setOrigin(0.5);

            if (tabs.length > 1) {
                button.setInteractive({ useHandCursor: true });
                button.on('pointerdown', () => {
                    if (this.mode !== tab.key) {
                        this.cleanup();
                        this.scene.restart({ mode: tab.key, previousSceneKey: this.previousSceneKey });
                    }
                });
                button.on('pointerover', () => {
                    if (this.mode !== tab.key) button.setFill('#aaaaaa');
                });
                button.on('pointerout', () => {
                    if (this.mode !== tab.key) button.setFill('#666666');
                });
            }

            this.modeButtons.push(button);
        });

        if (tabs.length > 1) {
            const line = this.add.graphics();
            line.lineStyle(1, 0x333355, 1);
            line.lineBetween(centerX - 220, tabY + 18, centerX + 220, tabY + 18);
            line.setDepth(0);
        }
    }
    
    showEquipmentMenu() {
        const vehicle = this.playerData.vehicle;
        if (!vehicle) {
            const noVehicleText = this.add.text(
                this.cameras.main.width / 2,
                this.cameras.main.height / 2,
                '没有战车\n按ESC返回',
                { 
                    fontSize: '20px', 
                    fill: '#ff0000', 
                    fontFamily: 'monospace',
                    align: 'center'
                }
            ).setOrigin(0.5);
            return;
        }
        
        // 完全清空之前的状态（包括主菜单）
        this.clearAllEquipmentMenuDisplay();
        
        // 显示装备槽位（可选择的列表）
        const startX = 100;
        const startY = 250;
        const spacing = 40;
        
        this.equipmentState.slotTexts = [];
        let y = startY;
        
        VehicleEquipmentSystem.SLOT_ORDER.forEach((slot, index) => {
            const slotName = VehicleEquipmentSystem.SLOT_NAMES[slot];
            const equipment = VehicleEquipmentSystem.getEquippedItem(vehicle, slot);
            
            let displayText = equipment ? 
                `✓ ${slotName}: ${equipment.name} (耐久: ${equipment.durability}/${equipment.maxDurability})` :
                `${slotName}: 未装备`;
            
            const required = VehicleEquipmentSystem.REQUIRED_SLOTS.includes(slot);
            if (required) {
                displayText += ' [必须]';
            }
            
            const color = UIStyleHelper.getMenuItemFill(
                index === this.equipmentState.selectedSlotIndex,
                { isInactive: !equipment }
            );
            
            const text = this.add.text(startX, y, displayText, {
                fontSize: '18px',
                fill: color,
                fontFamily: 'monospace'
            });
            
            // 添加鼠标交互
            text.setInteractive({ useHandCursor: true });
            text.on('pointerover', () => {
                this.equipmentState.selectedSlotIndex = index;
                this.updateSlotSelection();
            });
            text.on('pointerdown', () => {
                this.equipmentState.selectedSlotIndex = index;
                this.updateSlotSelection();
                this.handleSlotSelection();
            });
            
            this.equipmentState.slotTexts.push(text);
            y += spacing;
        });
        
        // 显示战车属性（右侧）- 使用更新方法避免重叠
        this.updateVehicleStatsDisplay(vehicle);
        
        // 显示提示信息（底部引导由 createGuidePanel 统一显示）
        if (this.equipmentState.statsTexts.hint) {
            this.equipmentState.statsTexts.hint.destroy();
            this.equipmentState.statsTexts.hint = null;
        }
        
        // 设置装备菜单的键盘控制
        this.setupEquipmentMenuControls();
    }
    
    /**
     * 清空装备菜单显示（只清空子菜单，保留主菜单）
     */
    clearEquipmentMenuDisplay() {
        if (this.equipmentState.optionTexts) {
            this.equipmentState.optionTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
            this.equipmentState.optionTexts = [];
        }
    }
    
    /**
     * 完全清空装备菜单显示（包括主菜单）
     */
    clearAllEquipmentMenuDisplay() {
        this.clearEquipmentMenuDisplay();
        // 清空主菜单的槽位文本
        if (this.equipmentState.slotTexts) {
            this.equipmentState.slotTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
            this.equipmentState.slotTexts = [];
        }
        // 清空属性文本
        this.clearStatsTexts();
    }
    
    /**
     * 清空属性文本
     */
    clearStatsTexts() {
        const statsTexts = this.equipmentState.statsTexts;
        Object.keys(statsTexts).forEach(key => {
            if (statsTexts[key] && statsTexts[key].destroy) {
                statsTexts[key].destroy();
            }
            statsTexts[key] = null;
        });
    }
    
    /**
     * 更新战车属性显示
     */
    updateVehicleStatsDisplay(vehicle) {
        const rightPanelX = this.cameras.main.width * 0.6;
        const startY = 250;
        const spacing = 40;
        let rightY = startY;
        
        const stats = VehicleEquipmentSystem.calculateVehicleStats(vehicle);
        
        // 战车属性标题
        if (!this.equipmentState.statsTexts.title) {
            this.equipmentState.statsTexts.title = this.add.text(rightPanelX, rightY, '--- 战车属性 ---', {
                fontSize: '20px',
                fill: '#4cc9f0',
                fontFamily: 'monospace'
            });
        }
        
        rightY += spacing;
        // 攻击力
        if (!this.equipmentState.statsTexts.attack) {
            this.equipmentState.statsTexts.attack = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.attack.setText(`攻击力: ${stats.attack}`);
        
        rightY += spacing - 10;
        // 防御力
        if (!this.equipmentState.statsTexts.defense) {
            this.equipmentState.statsTexts.defense = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.defense.setText(`防御力: ${stats.defense}`);
        
        rightY += spacing - 10;
        // SP值（当前/最大）
        if (!this.equipmentState.statsTexts.maxSp) {
            this.equipmentState.statsTexts.maxSp = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        const currentSp = vehicle.sp || 0;
        const maxSp = stats.maxSp || 0;
        // 根据SP百分比选择颜色
        const spPercent = maxSp > 0 ? currentSp / maxSp : 0;
        const spColor = spPercent > 0.6 ? '#ffffff' : (spPercent > 0.3 ? '#ffff00' : '#ff0000');
        this.equipmentState.statsTexts.maxSp.setFill(spColor);
        this.equipmentState.statsTexts.maxSp.setText(`SP值: ${currentSp}/${maxSp}`);
        
        rightY += spacing - 10;
        // 闪避率
        if (!this.equipmentState.statsTexts.dodgeRate) {
            this.equipmentState.statsTexts.dodgeRate = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.dodgeRate.setText(`闪避率: ${(stats.dodgeRate * 100).toFixed(1)}%`);
        
        // 重量和载重信息
        rightY += spacing;
        if (!this.equipmentState.statsTexts.weightTitle) {
            this.equipmentState.statsTexts.weightTitle = this.add.text(rightPanelX, rightY, '--- 重量信息 ---', {
                fontSize: '20px',
                fill: '#4cc9f0',
                fontFamily: 'monospace'
            });
        }
        
        rightY += spacing;
        // 计算总重量（包括装备重量和SP重量）
        const totalWeight = VehicleEquipmentSystem.calculateTotalWeightWithSp(vehicle);
        const loadCapacity = VehicleEquipmentSystem.getLoadCapacity(vehicle);
        if (!this.equipmentState.statsTexts.weight) {
            this.equipmentState.statsTexts.weight = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: totalWeight > loadCapacity ? '#ff0000' : (totalWeight / loadCapacity > 0.8 ? '#ffff00' : '#ffffff'),
                fontFamily: 'monospace'
            });
        }
        // 根据重量使用不同颜色：超过载重红色，接近载重黄色，正常白色
        const weightColor = loadCapacity > 0 && totalWeight > loadCapacity ? '#ff0000' : 
                           (loadCapacity > 0 && totalWeight / loadCapacity > 0.8 ? '#ffff00' : '#ffffff');
        this.equipmentState.statsTexts.weight.setFill(weightColor);
        if (loadCapacity > 0) {
            // 显示总重量（保留一位小数，因为SP重量可能是小数）
            this.equipmentState.statsTexts.weight.setText(`重量: ${totalWeight.toFixed(1)}/${loadCapacity}`);
        } else {
            this.equipmentState.statsTexts.weight.setText(`重量: ${totalWeight.toFixed(1)} (无载重限制)`);
        }
        
        // 弹药信息
        rightY += spacing;
        if (!this.equipmentState.statsTexts.ammoTitle) {
            this.equipmentState.statsTexts.ammoTitle = this.add.text(rightPanelX, rightY, '--- 弹药信息 ---', {
                fontSize: '20px',
                fill: '#4cc9f0',
                fontFamily: 'monospace'
            });
        }
        
        rightY += spacing;
        const cannonAmmo = vehicle.ammo?.cannon_ammo || 0;
        if (!this.equipmentState.statsTexts.cannonAmmo) {
            this.equipmentState.statsTexts.cannonAmmo = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.cannonAmmo.setText(`主炮弹药: ${cannonAmmo}`);
        
        rightY += spacing - 10;
        const seAmmo = vehicle.ammo?.se_ammo || 0;
        if (!this.equipmentState.statsTexts.seAmmo) {
            this.equipmentState.statsTexts.seAmmo = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.seAmmo.setText(`S-E弹药: ${seAmmo}`);
        
        // 抗腐蚀图层信息
        rightY += spacing;
        if (!this.equipmentState.statsTexts.corrosionTitle) {
            this.equipmentState.statsTexts.corrosionTitle = this.add.text(rightPanelX, rightY, '--- 抗腐蚀图层 ---', {
                fontSize: '20px',
                fill: '#4cc9f0',
                fontFamily: 'monospace'
            });
        }
        
        rightY += spacing;
        // 使用 VehicleEquipmentSystem 计算最大图层值（从底盘获取，确保值是最新的）
        const maxCorrosionLayer = VehicleEquipmentSystem.calculateMaxCorrosionLayer(vehicle);
        vehicle.maxCorrosionLayer = maxCorrosionLayer; // 更新最大值
        const corrosionLayer = vehicle.corrosionLayer || 0;
        if (!this.equipmentState.statsTexts.corrosionLayer) {
            this.equipmentState.statsTexts.corrosionLayer = this.add.text(rightPanelX, rightY, '', {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            });
        }
        this.equipmentState.statsTexts.corrosionLayer.setText(`图层: ${corrosionLayer}/${maxCorrosionLayer}`);
    }
    
    /**
     * 设置装备菜单的键盘控制
     */
    setupEquipmentMenuControls() {
        this._clearMenuInput();

        if (this.equipmentState.isActionMenuActive || this.equipmentState.isEquipableItemsMenuActive) {
            return;
        }

        this._bindMenuInput({
            onUp: () => {
                if (this.equipmentState.selectedSlotIndex > 0) {
                    this.equipmentState.selectedSlotIndex--;
                    this.updateSlotSelection();
                }
            },
            onDown: () => {
                if (this.equipmentState.selectedSlotIndex < VehicleEquipmentSystem.SLOT_ORDER.length - 1) {
                    this.equipmentState.selectedSlotIndex++;
                    this.updateSlotSelection();
                }
            },
            onSelect: () => this.handleSlotSelection()
        });
    }
    
    /**
     * 移除装备菜单的键盘监听
     */
    removeEquipmentMenuKeys() {
        this._clearMenuInput();
    }
    
    /**
     * 更新槽位选择显示
     */
    updateSlotSelection() {
        const vehicle = this.playerData.vehicle;
        if (!vehicle) return;
        
        VehicleEquipmentSystem.SLOT_ORDER.forEach((slot, index) => {
            const text = this.equipmentState.slotTexts[index];
            if (!text) return;
            
            const equipment = VehicleEquipmentSystem.getEquippedItem(vehicle, slot);
            const slotName = VehicleEquipmentSystem.SLOT_NAMES[slot];
            
            let displayText = equipment ? 
                `✓ ${slotName}: ${equipment.name} (耐久: ${equipment.durability}/${equipment.maxDurability})` :
                `${slotName}: 未装备`;
            
            const required = VehicleEquipmentSystem.REQUIRED_SLOTS.includes(slot);
            if (required) {
                displayText += ' [必须]';
            }
            
            const isSelected = index === this.equipmentState.selectedSlotIndex;
            const color = UIStyleHelper.getMenuItemFill(isSelected, { isInactive: !equipment });
            
            text.setText(displayText);
            text.setStyle({ fill: color });
        });
    }
    
    /**
     * 处理槽位选择
     */
    handleSlotSelection() {
        const vehicle = this.playerData.vehicle;
        if (!vehicle) return;
        
        const slot = VehicleEquipmentSystem.SLOT_ORDER[this.equipmentState.selectedSlotIndex];
        const equipment = VehicleEquipmentSystem.getEquippedItem(vehicle, slot);
        
        if (equipment) {
            // 已装备：显示操作菜单（卸下、替换）
            this.showEquipmentActionMenu(equipment, slot);
        } else {
            // 未装备：显示可装备物品列表
            this.showEquipableItems(slot);
        }
    }
    
    showCraftMenu() {
        // 显示碎片合成菜单（只显示有图纸的装备）
        const equipmentData = this.cache.json.get('vehicle_equipment');
        
        if (!equipmentData) {
            this.add.text(
                this.cameras.main.width / 2,
                this.cameras.main.height / 2,
                '装备数据未加载',
                { fontSize: '20px', fill: '#ff0000', fontFamily: 'monospace' }
            ).setOrigin(0.5);
            return;
        }
        
        // 获取玩家有图纸的可合成装备
        const craftableEquipments = BlueprintSystem.getCraftableEquipments(this.playerData, equipmentData);
        
        if (craftableEquipments.length === 0) {
            this.add.text(
                this.cameras.main.width / 2,
                this.cameras.main.height / 2,
                '没有可合成的装备\n（需要先获得装备图纸）',
                { 
                    fontSize: '20px', 
                    fill: '#888888', 
                    fontFamily: 'monospace',
                    align: 'center'
                }
            ).setOrigin(0.5);
            return;
        }
        
        // 显示可合成装备列表
        const startX = 100;
        const startY = 250;
        const spacing = 35;
        let y = startY;
        
        this.add.text(startX, y, '--- 可合成装备（需要图纸） ---', {
            fontSize: '20px',
            fill: '#4cc9f0',
            fontFamily: 'monospace'
        });
        
        y += spacing + 10;
        
        // 显示每个可合成装备的信息
        craftableEquipments.forEach(equipment => {
            const equipmentId = equipment.id;
            const tier = equipment.tier || 1;
            const check = FragmentSystem.canCraftEquipment(
                this.playerData, 
                equipmentId, 
                tier,
                equipment
            );
            
            // 装备名称
            const nameText = `${equipment.name}`;
            this.add.text(startX, y, nameText, {
                fontSize: '18px',
                fill: check.canCraft ? '#00ff00' : '#ffffff',
                fontFamily: 'monospace'
            });
            
            // 合成需求
            y += 20;
            const requirements = [];
            
            // 碎片需求（从craftMaterials读取）
            const requiredFragments = equipment.craftMaterials?.fragments || 0;
            requirements.push(`碎片: ${check.fragments}/${requiredFragments}`);
            
            // 材料需求（跳过fragments字段）
            if (equipment.craftMaterials) {
                const materials = MaterialSystem.getPlayerMaterials(this.playerData);
                Object.entries(equipment.craftMaterials).forEach(([materialId, requiredCount]) => {
                    // 跳过fragments字段，它不是材料
                    if (materialId === 'fragments') return;
                    
                    const playerCount = materials[materialId] || 0;
                    const materialName = MaterialSystem.getMaterialData(materialId)?.name || materialId;
                    const color = playerCount >= requiredCount ? '#00ff00' : '#ff0000';
                    requirements.push(`${materialName}: ${playerCount}/${requiredCount}`);
                });
            }
            
            const reqText = requirements.join('  |  ');
            this.add.text(startX + 20, y, reqText, {
                fontSize: '14px',
                fill: check.canCraft ? '#00ff00' : '#ffaa00',
                fontFamily: 'monospace'
            });
            
            // 状态提示
            y += 20;
            const statusText = check.canCraft ? '✓ 可以合成' : `✗ ${check.message}`;
            this.add.text(startX + 20, y, statusText, {
                fontSize: '14px',
                fill: check.canCraft ? '#00ff00' : '#ff0000',
                fontFamily: 'monospace'
            });
            
            y += spacing;
        });
        
        // 保存可合成装备列表（用于选择和交互）
        this.equipmentState.craftableEquipments = craftableEquipments;
        this.equipmentState.selectedCraftIndex = 0;
        
        // 清空之前的合成文本
        if (this.equipmentState.craftTexts) {
            this.equipmentState.craftTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
        }
        this.equipmentState.craftTexts = [];
        
        // 创建可选择的装备列表（简化版，只显示名称和状态）
        const listStartY = startY + 50;
        craftableEquipments.forEach((equipment, index) => {
            const equipmentId = equipment.id;
            const tier = equipment.tier || 1;
            const check = FragmentSystem.canCraftEquipment(
                this.playerData, 
                equipmentId, 
                tier,
                equipment
            );
            
            const yPos = listStartY + (index * 30);
            const displayText = `${equipment.name} ${check.canCraft ? '[可合成]' : '[材料不足]'}`;
            const color = index === this.equipmentState.selectedCraftIndex ? UIStyleHelper.MENU_COLORS.SELECTED : 
                         (check.canCraft ? '#ffffff' : '#888888');
            
            const text = this.add.text(startX, yPos, displayText, {
                fontSize: '16px',
                fill: color,
                fontFamily: 'monospace'
            });
            
            // 添加鼠标交互
            text.setInteractive({ useHandCursor: true });
            text.on('pointerover', () => {
                this.equipmentState.selectedCraftIndex = index;
                this.updateCraftSelection();
            });
            text.on('pointerdown', () => {
                this.equipmentState.selectedCraftIndex = index;
                this.updateCraftSelection();
                this.executeCraft(equipment);
            });
            
            this.equipmentState.craftTexts.push(text);
        });
        
        // 设置合成菜单的键盘控制
        this.setupCraftMenuControls();
        
        // 显示提示信息
        y = this.cameras.main.height - 50;
        this.add.text(
            this.cameras.main.width / 2,
            y,
            '↑↓选择装备, 空格键合成, ESC返回',
            {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace'
            }
        ).setOrigin(0.5);
    }
    
    /**
     * 更新合成选择显示
     */
    updateCraftSelection() {
        if (!this.equipmentState.craftableEquipments || !this.equipmentState.craftTexts) return;
        
        this.equipmentState.craftableEquipments.forEach((equipment, index) => {
            const text = this.equipmentState.craftTexts[index];
            if (!text) return;
            
            const equipmentId = equipment.id;
            const tier = equipment.tier || 1;
            const check = FragmentSystem.canCraftEquipment(
                this.playerData, 
                equipmentId, 
                tier,
                equipment
            );
            
            const displayText = `${equipment.name} ${check.canCraft ? '[可合成]' : '[材料不足]'}`;
            const isSelected = index === this.equipmentState.selectedCraftIndex;
            const color = isSelected ? UIStyleHelper.MENU_COLORS.SELECTED : 
                         (check.canCraft ? '#ffffff' : '#888888');
            
            text.setText(displayText);
            text.setStyle({ fill: color });
        });
    }
    
    /**
     * 设置合成菜单的键盘控制
     */
    setupCraftMenuControls() {
        this.removeEquipmentMenuKeys();
        this.removeCraftMenuKeys();

        this._bindMenuInput({
            onUp: () => {
                if (this.equipmentState.selectedCraftIndex > 0) {
                    this.equipmentState.selectedCraftIndex--;
                    this.updateCraftSelection();
                }
            },
            onDown: () => {
                if (this.equipmentState.selectedCraftIndex < this.equipmentState.craftableEquipments.length - 1) {
                    this.equipmentState.selectedCraftIndex++;
                    this.updateCraftSelection();
                }
            },
            onSelect: () => {
                const equipment = this.equipmentState.craftableEquipments[this.equipmentState.selectedCraftIndex];
                if (equipment) {
                    this.executeCraft(equipment);
                }
            }
        });
    }
    
    /**
     * 移除合成菜单的键盘监听
     */
    removeCraftMenuKeys() {
        this._clearMenuInput();
    }
    
    /**
     * 执行合成
     */
    executeCraft(equipment) {
        const tier = equipment.tier || 1;
        const result = FragmentSystem.craftEquipment(this.playerData, equipment, tier);
        
        if (result.success) {
            this.showMessage(result.message, '#00ff00');
            // 刷新合成菜单
            this.time.delayedCall(500, () => {
                this.showCraftMenu();
            });
        } else {
            this.showMessage(result.message, '#ff0000');
        }
    }
    
    showDisassembleMenu() {
        // 清空之前的所有分解菜单文本（包括材料统计、标题、装备列表、提示信息等）
        if (this.equipmentState.disassembleMenuTexts) {
            this.equipmentState.disassembleMenuTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
        }
        this.equipmentState.disassembleMenuTexts = [];
        
        // 清空之前的分解装备列表文本
        if (this.equipmentState.disassembleTexts) {
            this.equipmentState.disassembleTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
        }
        this.equipmentState.disassembleTexts = [];
        
        // 显示材料分解菜单
        const materials = MaterialSystem.getPlayerMaterials(this.playerData);
        
        const startX = 100;
        const startY = 250;
        const spacing = 30;
        
        // 材料统计标题
        const materialTitleText = this.add.text(startX, startY, '--- 材料统计 ---', {
            fontSize: '20px',
            fill: '#4cc9f0',
            fontFamily: 'monospace'
        });
        this.equipmentState.disassembleMenuTexts.push(materialTitleText);
        
        let y = startY + spacing + 10;
        
        if (Object.keys(materials).length === 0) {
            const noMaterialText = this.add.text(startX, y, '没有材料', {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace'
            });
            this.equipmentState.disassembleMenuTexts.push(noMaterialText);
        } else {
            Object.entries(materials).forEach(([materialId, count]) => {
                const materialData = MaterialSystem.getMaterialData(materialId);
                const materialText = this.add.text(startX, y, `${materialData.name}: ${count}`, {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace'
                });
                this.equipmentState.disassembleMenuTexts.push(materialText);
                y += spacing;
            });
        }
        
        // 显示背包中的装备（可分解）
        y += 40;
        const equipmentTitleText = this.add.text(startX, y, '--- 可分解装备 ---', {
            fontSize: '20px',
            fill: '#4cc9f0',
            fontFamily: 'monospace'
        });
        this.equipmentState.disassembleMenuTexts.push(equipmentTitleText);
        
        y += spacing + 10;
        
        const vehicleEquipment = this.playerData.inventory?.filter(
            item => item.type === 'vehicle_equipment'
        ) || [];
        
        if (vehicleEquipment.length === 0) {
            const noEquipmentText = this.add.text(startX, y, '背包中没有战车装备', {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace'
            });
            this.equipmentState.disassembleMenuTexts.push(noEquipmentText);
        } else {
            // 保存可分解装备列表
            this.equipmentState.disassembleableEquipments = vehicleEquipment;
            this.equipmentState.selectedDisassembleIndex = 0;
            
            // 创建可选择的装备列表
            vehicleEquipment.forEach((equipment, index) => {
                const tier = equipment.tier || 1;
                const tierKey = `tier${tier}`;
                const materials = MaterialSystem.DISASSEMBLE_TABLE[tierKey] || MaterialSystem.DISASSEMBLE_TABLE.tier1;
                const materialText = Object.entries(materials)
                    .map(([id, count]) => `${MaterialSystem.getMaterialData(id).name} x${count}`)
                    .join(', ');
                
                const displayText = `${equipment.name} → ${materialText}`;
                const color = index === this.equipmentState.selectedDisassembleIndex
                    ? UIStyleHelper.MENU_COLORS.SELECTED
                    : UIStyleHelper.MENU_COLORS.NORMAL;
                
                const text = this.add.text(startX, y, displayText, {
                    fontSize: '16px',
                    fill: color,
                    fontFamily: 'monospace'
                });
                
                // 添加鼠标交互
                text.setInteractive({ useHandCursor: true });
                text.on('pointerover', () => {
                    this.equipmentState.selectedDisassembleIndex = index;
                    this.updateDisassembleSelection();
                });
                text.on('pointerdown', () => {
                    this.equipmentState.selectedDisassembleIndex = index;
                    this.updateDisassembleSelection();
                    this.executeDisassemble(equipment);
                });
                
                this.equipmentState.disassembleTexts.push(text);
                y += spacing;
            });
        }
        
        // 设置分解菜单的键盘控制
        this.setupDisassembleMenuControls();
        
        // 显示提示信息
        const hintY = this.cameras.main.height - 50;
        const hintText = this.add.text(
            this.cameras.main.width / 2,
            hintY,
            '↑↓选择装备, 空格键分解, ESC返回',
            {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace'
            }
        ).setOrigin(0.5);
        this.equipmentState.disassembleMenuTexts.push(hintText);
    }
    
    /**
     * 更新分解选择显示
     */
    updateDisassembleSelection() {
        if (!this.equipmentState.disassembleableEquipments || !this.equipmentState.disassembleTexts) return;
        
        this.equipmentState.disassembleableEquipments.forEach((equipment, index) => {
            const text = this.equipmentState.disassembleTexts[index];
            if (!text) return;
            
            const tier = equipment.tier || 1;
            const tierKey = `tier${tier}`;
            const materials = MaterialSystem.DISASSEMBLE_TABLE[tierKey] || MaterialSystem.DISASSEMBLE_TABLE.tier1;
            const materialText = Object.entries(materials)
                .map(([id, count]) => `${MaterialSystem.getMaterialData(id).name} x${count}`)
                .join(', ');
            
            const displayText = `${equipment.name} → ${materialText}`;
            const isSelected = index === this.equipmentState.selectedDisassembleIndex;
            const color = isSelected ? UIStyleHelper.MENU_COLORS.SELECTED : '#ffffff';
            
            text.setText(displayText);
            text.setStyle({ fill: color });
        });
    }
    
    /**
     * 设置分解菜单的键盘控制
     */
    setupDisassembleMenuControls() {
        this.removeEquipmentMenuKeys();
        this.removeCraftMenuKeys();
        this.removeDisassembleMenuKeys();

        this._bindMenuInput({
            onUp: () => {
                if (this.equipmentState.selectedDisassembleIndex > 0) {
                    this.equipmentState.selectedDisassembleIndex--;
                    this.updateDisassembleSelection();
                }
            },
            onDown: () => {
                if (this.equipmentState.selectedDisassembleIndex < this.equipmentState.disassembleableEquipments.length - 1) {
                    this.equipmentState.selectedDisassembleIndex++;
                    this.updateDisassembleSelection();
                }
            },
            onSelect: () => {
                const equipment = this.equipmentState.disassembleableEquipments[this.equipmentState.selectedDisassembleIndex];
                if (equipment) {
                    this.executeDisassemble(equipment);
                }
            }
        });
    }
    
    /**
     * 移除分解菜单的键盘监听
     */
    removeDisassembleMenuKeys() {
        this._clearMenuInput();
    }
    
    /**
     * 执行分解
     */
    executeDisassemble(equipment) {
        const result = MaterialSystem.disassembleEquipment(this.playerData, equipment);
        
        if (result.success) {
            this.showMessage(result.message, '#00ff00');
            // 刷新分解菜单
            this.time.delayedCall(500, () => {
                this.showDisassembleMenu();
            });
        } else {
            this.showMessage(result.message, '#ff0000');
        }
    }
    
    /**
     * 显示装备操作菜单
     */
    showEquipmentActionMenu(equipment, slot) {
        this.equipmentState.isActionMenuActive = true;
        this.equipmentState.currentEquipmentItem = equipment;
        this.equipmentState.currentSlot = slot;
        
        // 移除装备菜单的键盘控制
        this.removeEquipmentMenuKeys();
        
        // 清空当前显示
        this.clearEquipmentMenuDisplay();
        
        // 创建操作选项
        const actions = ['卸下', '替换', '返回'];
        this.equipmentState.actionMenuSelected = 0;
        
        // 创建操作菜单背景
        const actionMenuBg = this.add.graphics();
        actionMenuBg.fillStyle(0x000000, 0.7);
        actionMenuBg.fillRoundedRect(
            this.cameras.main.centerX - 150,
            this.cameras.main.centerY - 100,
            300,
            200,
            10
        );
        actionMenuBg.setScrollFactor(0);
        actionMenuBg.setDepth(10002);
        this.equipmentState.optionTexts.push(actionMenuBg);
        
        // 显示操作选项
        const actionMenuTexts = [];
        actions.forEach((action, index) => {
            const y = this.cameras.main.centerY - 50 + index * 40;
            const text = this.add.text(
                this.cameras.main.centerX,
                y,
                action,
                {
                    fontSize: '18px',
                    fill: UIStyleHelper.getMenuItemFill(index === 0),
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            text.setScrollFactor(0);
            text.setDepth(10003);
            
            text.setInteractive({ useHandCursor: true });
            text.on('pointerover', () => {
                this.equipmentState.actionMenuSelected = index;
                this.updateActionMenuSelection(actionMenuTexts);
            });
            text.on('pointerdown', () => {
                this.equipmentState.actionMenuSelected = index;
                this.updateActionMenuSelection(actionMenuTexts);
                this.executeEquipmentAction(actions[index]);
            });
            
            this.equipmentState.optionTexts.push(text);
            actionMenuTexts.push(text);
        });
        
        // 保存actionMenuTexts引用
        this.equipmentState.actionMenuTexts = actionMenuTexts;
        
        // 提示文本
        const hintText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY + 120,
            `当前装备: ${equipment.name}\n(↑↓选择, 空格键确认, ESC返回)`,
            {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: 'monospace',
                align: 'center'
            }
        ).setOrigin(0.5);
        hintText.setScrollFactor(0);
        hintText.setDepth(10003);
        this.equipmentState.optionTexts.push(hintText);
        
        // 设置输入
        this.setupActionMenuControls();
    }
    
    /**
     * 更新装备操作菜单选择
     */
    updateActionMenuSelection(actionMenuTexts) {
        if (actionMenuTexts) {
            actionMenuTexts.forEach((text, index) => {
                if (text && text.active !== false) {
                    text.setStyle({ 
                        fill: UIStyleHelper.getMenuItemFill(
                            index === this.equipmentState.actionMenuSelected
                        )
                    });
                }
            });
        }
    }
    
    /**
     * 设置装备操作菜单控制
     */
    setupActionMenuControls() {
        this.removeEquipmentMenuKeys();

        this._bindMenuInput({
            blockWhen: () => !this.equipmentState.isActionMenuActive,
            onUp: () => {
                if (this.equipmentState.actionMenuSelected > 0) {
                    this.equipmentState.actionMenuSelected--;
                    if (this.equipmentState.actionMenuTexts) {
                        this.updateActionMenuSelection(this.equipmentState.actionMenuTexts);
                    }
                }
            },
            onDown: () => {
                if (this.equipmentState.actionMenuSelected < 2) {
                    this.equipmentState.actionMenuSelected++;
                    if (this.equipmentState.actionMenuTexts) {
                        this.updateActionMenuSelection(this.equipmentState.actionMenuTexts);
                    }
                }
            },
            onSelect: () => {
                const actions = ['卸下', '替换', '返回'];
                this.executeEquipmentAction(actions[this.equipmentState.actionMenuSelected]);
            },
            onCancel: () => {
                this.closeEquipmentActionMenu();
            }
        });
    }
    
    /**
     * 移除装备操作菜单的键盘监听
     */
    removeActionMenuKeys() {
        this._clearMenuInput();
    }
    
    /**
     * 执行装备操作
     */
    executeEquipmentAction(action) {
        switch(action) {
            case '卸下':
                this.unequipVehicleItem();
                break;
            case '替换':
                this.showEquipableItems(this.equipmentState.currentSlot);
                break;
            case '返回':
                this.closeEquipmentActionMenu();
                break;
        }
    }
    
    /**
     * 显示可装备物品列表
     */
    showEquipableItems(slot) {
        this.equipmentState.isActionMenuActive = false;
        this.equipmentState.isEquipableItemsMenuActive = true;
        this.equipmentState.currentSlot = slot;
        
        // 移除之前的键盘监听
        this.removeActionMenuKeys();
        
        // 从背包中筛选可装备的战车装备
        const equipableItems = (this.playerData.inventory || []).filter(item => {
            if (!item) return false;
            return item.type === 'vehicle_equipment' && item.slot === slot;
        });
        
        if (equipableItems.length === 0) {
            this.showMessage('背包中没有可装备的战车装备', '#ffaa00');
            // 如果是从装备操作菜单进入的，返回到装备操作菜单
            if (this.equipmentState.currentEquipmentItem) {
                this.showEquipmentActionMenu(this.equipmentState.currentEquipmentItem, slot);
            } else {
                this.closeEquipableItemsMenu();
                this.showEquipmentMenu();
            }
            return;
        }
        
        // 清空当前显示
        this.clearEquipmentMenuDisplay();
        
        this.equipmentState.equipableItemsList = equipableItems;
        this.equipmentState.equipableItemSelected = 0;
        
        // 创建可装备物品列表背景
        const equipableMenuBg = this.add.graphics();
        const menuHeight = Math.min((equipableItems.length + 1) * 35 + 100, 500);
        equipableMenuBg.fillStyle(0x000000, 0.7);
        equipableMenuBg.fillRoundedRect(
            this.cameras.main.centerX - 250,
            this.cameras.main.centerY - 150,
            500,
            menuHeight,
            10
        );
        equipableMenuBg.setScrollFactor(0);
        equipableMenuBg.setDepth(10002);
        this.equipmentState.optionTexts.push(equipableMenuBg);
        
        // 显示物品列表
        const equipableItemTexts = [];
        const itemsWithReturn = [...equipableItems, { name: '返回', isReturn: true }];
        
        itemsWithReturn.forEach((item, index) => {
            const y = this.cameras.main.centerY - 100 + index * 35;
            let displayText = item.name;
            
            if (!item.isReturn) {
                // 显示装备属性
                if (item.damage) {
                    displayText += ` (攻击+${item.damage})`;
                } else if (item.defense) {
                    displayText += ` (防御+${item.defense})`;
                }
                if (item.durability !== undefined) {
                    displayText += ` [耐久: ${item.durability}/${item.maxDurability || item.durability}]`;
                }
                if (item.weight !== undefined) {
                    displayText += ` [重量: ${item.weight}]`;
                }
            }
            
            const text = this.add.text(
                this.cameras.main.centerX,
                y,
                displayText,
                {
                    fontSize: '16px',
                    fill: UIStyleHelper.getMenuItemFill(index === 0),
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            text.setScrollFactor(0);
            text.setDepth(10003);
            
            text.setInteractive({ useHandCursor: true });
            text.on('pointerover', () => {
                this.equipmentState.equipableItemSelected = index;
                this.updateEquipableItemSelection(equipableItemTexts);
            });
            text.on('pointerdown', () => {
                this.equipmentState.equipableItemSelected = index;
                this.updateEquipableItemSelection(equipableItemTexts);
                if (item.isReturn) {
                    this.closeEquipableItemsMenu();
                    if (this.equipmentState.currentEquipmentItem) {
                        this.showEquipmentActionMenu(this.equipmentState.currentEquipmentItem, slot);
                    } else {
                        this.showEquipmentMenu();
                    }
                } else {
                    this.equipItemFromInventory(equipableItems[index]);
                }
            });
            
            this.equipmentState.optionTexts.push(text);
            equipableItemTexts.push(text);
        });
        
        // 保存equipableItemTexts引用
        this.equipmentState.equipableItemTexts = equipableItemTexts;
        
        // 提示文本
        const hintText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY + 150,
            `选择要装备的战车装备 (↑↓选择, 空格键装备, ESC返回)`,
            {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: 'monospace'
            }
        ).setOrigin(0.5);
        hintText.setScrollFactor(0);
        hintText.setDepth(10003);
        this.equipmentState.optionTexts.push(hintText);
        
        // 设置输入
        this.setupEquipableItemsControls();
    }
    
    /**
     * 更新可装备物品选择
     */
    updateEquipableItemSelection(equipableItemTexts) {
        if (equipableItemTexts) {
            equipableItemTexts.forEach((text, index) => {
                if (text && text.active !== false) {
                    text.setStyle({ 
                        fill: UIStyleHelper.getMenuItemFill(
                            index === this.equipmentState.equipableItemSelected
                        ) 
                    });
                }
            });
        }
    }
    
    /**
     * 设置可装备物品菜单控制
     */
    setupEquipableItemsControls() {
        this.removeActionMenuKeys();

        this._bindMenuInput({
            blockWhen: () => !this.equipmentState.isEquipableItemsMenuActive,
            onUp: () => {
                if (this.equipmentState.equipableItemSelected > 0) {
                    this.equipmentState.equipableItemSelected--;
                    if (this.equipmentState.equipableItemTexts) {
                        this.updateEquipableItemSelection(this.equipmentState.equipableItemTexts);
                    }
                }
            },
            onDown: () => {
                if (this.equipmentState.equipableItemSelected < this.equipmentState.equipableItemsList.length) {
                    this.equipmentState.equipableItemSelected++;
                    if (this.equipmentState.equipableItemTexts) {
                        this.updateEquipableItemSelection(this.equipmentState.equipableItemTexts);
                    }
                }
            },
            onSelect: () => {
                if (this.equipmentState.equipableItemSelected === this.equipmentState.equipableItemsList.length) {
                    this.closeEquipableItemsMenu();
                    if (this.equipmentState.currentEquipmentItem) {
                        this.showEquipmentActionMenu(this.equipmentState.currentEquipmentItem, this.equipmentState.currentSlot);
                    } else {
                        this.showEquipmentMenu();
                    }
                } else {
                    this.equipItemFromInventory(this.equipmentState.equipableItemsList[this.equipmentState.equipableItemSelected]);
                }
            },
            onCancel: () => {
                this.closeEquipableItemsMenu();
                if (this.equipmentState.currentEquipmentItem) {
                    this.showEquipmentActionMenu(this.equipmentState.currentEquipmentItem, this.equipmentState.currentSlot);
                } else {
                    this.showEquipmentMenu();
                }
            }
        });
    }
    
    /**
     * 移除可装备物品菜单的键盘监听
     */
    removeEquipableItemsKeys() {
        this._clearMenuInput();
    }
    
    /**
     * 从背包装备物品
     */
    equipItemFromInventory(item) {
        if (!item) return;
        
        const vehicle = this.playerData.vehicle;
        if (!vehicle) {
            this.showMessage('没有战车', '#ff0000');
            return;
        }
        
        const result = VehicleEquipmentSystem.equipItem(item, vehicle);
        
        if (result.success) {
            // 从背包移除
            InventoryHelper.removeFromInventory(item, this.playerData.inventory);
            
            // 如果有旧装备，放回背包
            if (result.oldEquipment) {
                InventoryHelper.addToInventory(result.oldEquipment, this.playerData.inventory);
            }
            
            this.showMessage(result.message, '#00ff00');
            
            // 更新菜单
            this.time.delayedCall(500, () => {
                this.closeEquipableItemsMenu();
                this.equipmentState.currentEquipmentItem = null;
                this.showEquipmentMenu();
            });
        } else {
            this.showMessage(result.message, '#ff0000');
        }
    }
    
    /**
     * 卸下战车装备
     */
    unequipVehicleItem() {
        const vehicle = this.playerData.vehicle;
        if (!vehicle) {
            this.showMessage('没有战车', '#ff0000');
            return;
        }
        
        const slot = this.equipmentState.currentSlot;
        const result = VehicleEquipmentSystem.unequipItem(vehicle, slot);
        
        if (result.success) {
            // 将装备放回背包
            InventoryHelper.addToInventory(result.item, this.playerData.inventory);
            
            this.showMessage(result.message, '#00ff00');
            
            // 更新菜单
            this.time.delayedCall(500, () => {
                this.closeEquipmentActionMenu();
                this.showEquipmentMenu();
            });
        } else {
            this.showMessage(result.message, '#ff0000');
        }
    }
    
    /**
     * 关闭装备操作菜单
     */
    closeEquipmentActionMenu() {
        this.equipmentState.isActionMenuActive = false;
        this.removeActionMenuKeys();
        this.clearEquipmentMenuDisplay();
        this.equipmentState.currentEquipmentItem = null;
        this.equipmentState.currentSlot = null;
        this.showEquipmentMenu();
    }
    
    /**
     * 关闭可装备物品菜单
     */
    closeEquipableItemsMenu() {
        this.equipmentState.isEquipableItemsMenuActive = false;
        this.removeEquipableItemsKeys();
        this.clearEquipmentMenuDisplay();
        this.equipmentState.equipableItemsList = [];
        this.equipmentState.equipableItemSelected = 0;
    }
    
    /**
     * 显示消息提示
     */
    showMessage(message, color = '#ffffff') {
        // 移除之前的消息
        if (this.messageText) {
            this.messageText.destroy();
        }
        
        // 显示新消息
        this.messageText = this.add.text(
            this.cameras.main.width / 2,
            this.cameras.main.height - 100,
            message,
            {
                fontSize: '18px',
                fill: color,
                fontFamily: 'monospace',
                align: 'center'
            }
        ).setOrigin(0.5);
        this.messageText.setScrollFactor(0);
        this.messageText.setDepth(10004);
        
        // 3秒后自动移除
        this.time.delayedCall(3000, () => {
            if (this.messageText) {
                this.messageText.destroy();
                this.messageText = null;
            }
        });
    }
    
    setupControls() {
        this._globalEscInput = InputHelper.bindMenuKeyboard(this, {
            onCancel: () => {
                if (this.equipmentState.isActionMenuActive) {
                    this.closeEquipmentActionMenu();
                    return;
                }
                if (this.equipmentState.isEquipableItemsMenuActive) {
                    this.closeEquipableItemsMenu();
                    if (this.equipmentState.currentEquipmentItem) {
                        this.showEquipmentActionMenu(this.equipmentState.currentEquipmentItem, this.equipmentState.currentSlot);
                    } else {
                        this.showEquipmentMenu();
                    }
                    return;
                }
                if (this.mode === 'craft' || this.mode === 'disassemble') {
                    this.exitScene();
                    return;
                }
                this.exitScene();
            }
        }, { vertical: false, horizontal: false, confirm: false });
    }
    
    exitScene() {
        // 清理所有键盘监听
        this.cleanup();
        
        // 如果是从菜单场景来的，恢复菜单场景；否则恢复之前的场景
        if (this.previousSceneKey === 'MenuScene') {
            this.scene.stop();
            this.scene.resume('MenuScene');
        } else {
            // 使用resume而不是start，避免场景重新初始化导致位置重置和BGM重复播放
            this.scene.stop();
            this.scene.resume(this.previousSceneKey, {
                from: 'VehicleEquipmentScene'
            });
        }
    }
    
    /**
     * 清理资源
     */
    cleanup() {
        this._clearMenuInput();
        if (this._globalEscInput) {
            this._globalEscInput.destroy();
            this._globalEscInput = null;
        }
        this.removeEquipmentMenuKeys();
        this.removeActionMenuKeys();
        this.removeEquipableItemsKeys();
        this.removeCraftMenuKeys();
        this.removeDisassembleMenuKeys();
        this.clearAllEquipmentMenuDisplay();
        
        // 清理分解菜单的文本
        if (this.equipmentState.disassembleMenuTexts) {
            this.equipmentState.disassembleMenuTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
            this.equipmentState.disassembleMenuTexts = [];
        }
        if (this.equipmentState.disassembleTexts) {
            this.equipmentState.disassembleTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
            this.equipmentState.disassembleTexts = [];
        }
        
        if (this.messageText) {
            this.messageText.destroy();
            this.messageText = null;
        }
    }
}

